import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import { useState } from "react";
import { ChangePickupDateInput } from "@/Features/ChangePickupDate/Inputs/ChangePickupDateInput";

interface ChangePickupDatePreBookingDialogProps extends DialogBaseProps {
  shipment: PreBookingShipment;
  /** Fired with the new date once the change has been saved server-side. */
  onChanged: (newPickupDate: string) => void;
}

export function ChangePickupDatePreBookingDialog(
  props: ChangePickupDatePreBookingDialogProps,
) {
  const [processing, setProcessing] = useState(false);
  const [date, setDate] = useState(props.shipment.pickupDate);
  const [errorNotice, setErrorNotice] = useState(false);
  const shipmentsApi = useShipmentsApi();

  async function onConfirm(): Promise<DialogResult> {
    setProcessing(true);
    try {
      await shipmentsApi.changePickupDatePreBooking({
        changePickupDatePreBookingRequest: {
          shipmentId: props.shipment.shipmentId,
          pickupDate: date,
        },
      });
      setProcessing(false);
      props.onChanged(date);
      return DialogResult.Close;
    } catch {
      setProcessing(false);
      setErrorNotice(true);
      return DialogResult.KeepOpen;
    }
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Change Pickup Date"
        description="Select the updated date when your shipment will be ready for pickup."
        width="medium"
        okText="Change Pickup Date"
        okLoading={processing}
        onOk={onConfirm}
      >
        <ControlPresentationProvider presentation="underlined">
          <ChangePickupDateInput
            country={props.shipment.pickupLocation.address.countryCode}
            currentPickupDate={props.shipment.pickupDate}
            date={date}
            onDateChange={function (m) {
              setDate(m.format("YYYY-MM-DD"));
            }}
          />
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={errorNotice}
        onOpenChange={(open) => {
          if (!open) setErrorNotice(false);
        }}
        title="Error"
        footer={
          <Button primary onClick={() => setErrorNotice(false)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          Sorry, something went wrong. Our team has been alerted.
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
